import { useContext, useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
    Divider,
    Grid,
    List,
    ListItem,
    LinearProgress,
    Box,
    Typography,
    Chip,
    IconButton,
    Button,
    TextField,
    InputAdornment
} from "@mui/material";
import { red, blue, green, orange } from "@mui/material/colors";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Layout from "../components/Layout/Layout";
import DateSelect from "../components/DateSelect/index";
import ErrorPage from "./ErrorPage";
import { UserDataContext } from "../app/App";
import { useFetchPickingLinesQuery } from "../api";
import { PICKING_LINE_STATUS } from "../constants";

const Warehouse = () => {
    const { user } = useContext(UserDataContext);
    const history = useHistory();
    const { t } = useTranslation();

    const getDateTomorrow = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return tomorrow;
    };

    // Local state
    const [isAdminView, setIsAdminView] = useState(false);
    const [selectedDate, setSelectedDate] = useState(getDateTomorrow());
    const [searchText, setSearchText] = useState("");

    // Hooks
    const useFetchAllPickingLines = () => {
        const { data: dateFilteredData, isLoading: isDateLoading, isError: isDateError } = useFetchPickingLinesQuery(selectedDate.toISOString());
        const { data: allData, isLoading: isAllLoading, isError: isAllError } = useFetchPickingLinesQuery(null, { enabled: !searchText });

        return {
            data: searchText ? allData : dateFilteredData,
            isLoading: searchText ? isAllLoading : isDateLoading,
            isError: searchText ? isAllError : isDateError
        };
    };
    const { data, isLoading, isError } = useFetchAllPickingLines();             // Get picking lines to display

    // Handlers
    const handleLineClick = (lineId) => history.push(`/warehouse/${lineId}`);   // Navigate to line details
    const handleSearchChange = (event) => setSearchText(event.target.value);    // Change search string
    const handleAdminToggle = () => setIsAdminView((prev) => !prev);            // Set admin mode

    // Helpers
    const mapStatusToColoredLabel = (status) => {
        const statusMap = {
            [PICKING_LINE_STATUS.I_HILLU]: { label: "Í hillu", color: blue[500] },
            [PICKING_LINE_STATUS.I_HOLFI]: { label: "Í hólfi", color: orange[500] },
            [PICKING_LINE_STATUS.I_YFIRFERD]: { label: "Í yfirferð", color: red[500] },
            [PICKING_LINE_STATUS.KOMID_I_HILLU]: { label: "Komið í hillu", color: green[500] },
        };

        return statusMap[status] || { label: "Unknown", color: "grey" };
    };

    const filterPickingLines = useCallback((pickingLines) => {
        return pickingLines.filter((line) => {

            const matchesSearch = searchText
                ? line.project?.address?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                line.project?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                line.name?.toLowerCase().includes(searchText.toLowerCase())
                : true;

            if (isAdminView) {
                return matchesSearch;
            } else if (searchText) {
                return matchesSearch && line.status !== PICKING_LINE_STATUS.KOMID_I_HILLU;
            } else {
                return line.status === PICKING_LINE_STATUS.I_HILLU;
            }
        });
    }, [isAdminView, searchText]);

    const sortPickingLines = useCallback((pickingLines) => {
        return pickingLines.sort((a, b) => {
            if (!searchText) {
                return (a.employee?.name || "").localeCompare(b.employee?.name || "");
            }
            const addressA = a.project?.address?.name || a.project?.name || a.name || "";
            const addressB = b.project?.address?.name || b.project?.name || b.name || "";
            if (addressA !== addressB) {
                return addressA.localeCompare(addressB);
            }
            return dayjs(b.dueDate).valueOf() - dayjs(a.dueDate).valueOf();
        });
    }, [searchText]);

    const filteredAndSortedPickingLines = useMemo(() => {
        const allPickingLines = data || [];
        const filteredLines = filterPickingLines(allPickingLines);
        return sortPickingLines(filteredLines);
    }, [data, filterPickingLines, sortPickingLines]);

    if (isError) return <ErrorPage />;

    const totalCount = filteredAndSortedPickingLines ? filteredAndSortedPickingLines.length : 0;
    const isTomorrowSelected = () => selectedDate.getTime() === getDateTomorrow().getTime();

    return (
        <Layout color="rgb(249 98 125)" title={t("Warehouse")} subtitle={user.name} onAdminToggle={handleAdminToggle}>
            <Box display="flex" flexDirection="column" style={{ gap: "1rem" }}>

                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t("Search ...")}
                    value={searchText}
                    onChange={handleSearchChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchText && (
                                    <IconButton
                                        onClick={() => setSearchText("")}  // Clears the text
                                        aria-label="clear search"
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />

                <Box display="flex" flexDirection="column" style={{ gap: "0.3rem" }}>
                    <Box style={{ overflowX: "scroll" }}>
                        <DateSelect date={selectedDate} setDate={setSelectedDate} disabled={searchText.length > 0} />
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Button
                                onClick={() => setSelectedDate(getDateTomorrow())}
                                variant={isTomorrowSelected() ? "contained" : "outlined"}
                                style={isTomorrowSelected() && !searchText ?
                                    { color: "#fff", backgroundColor: "#000", borderRadius: 50 } : { borderRadius: 50 }}
                                size="small"
                                disabled={searchText.length > 0} 
                            >
                                {`${t("Today")}`}
                            </Button>
                        </Box>
                        {!searchText && 
                            <Typography variant="subtitle1" color="textSecondary">
                                {`${t("DueDate")}: ${dayjs(selectedDate).format("DD.MM.YY")}`}
                            </Typography>
                        }
                    </Box>
                </Box>

                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Typography variant="subtitle1">
                            <Typography variant="h5" component="span" color="primary" style={{ fontWeight: "bold", paddingRight: "8px" }}>
                                {totalCount}
                            </Typography>
                            {totalCount === 1 ? t("WarehouseRequest") : t("WarehouseRequests")}
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Divider style={{ backgroundColor: "darkgray" }} />
                    </Grid>

                    <Grid item>
                        {isLoading ? (
                            <LinearProgress />
                        ) : (
                            <List>
                                {filteredAndSortedPickingLines.map((line) => {
                                    const status = mapStatusToColoredLabel(line.status);

                                    return (
                                        <Box key={line.id} pb={1}>
                                            <ListItem key={line.id} style={{ borderLeft: `6px solid ${status.color}`, paddingRight: 0 }}>
                                                <Grid container style={{ justifyContent: "space-between" }}>
                                                    <Grid item xs={11} container direction="column" spacing={1}>
                                                        <Box display="flex" flexDirection="column" width="100%">
                                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                <Box display="flex" alignItems="center">
                                                                    <LocationOnIcon fontSize="small" />
                                                                    <Typography variant="body1" style={{ paddingLeft: "8px" }}>
                                                                        {line?.project?.name || line?.name || "N/A"}
                                                                    </Typography>
                                                                </Box>
                                                                <Box style={{ alignSelf: "flex-start" }}>
                                                                    <Chip size="small" label={status.label} style={{ backgroundColor: status.color, color: "#fff" }} />
                                                                </Box>
                                                            </Box>
                                                            <Box display="flex" alignItems="center" style={{ paddingTop: 4 }}>
                                                                <InboxIcon fontSize="small" style={{ marginRight: "8px" }} />
                                                                <Typography variant="body2">{line?.employee?.name || "N/A"}</Typography>
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {`Due: ${dayjs(line.dueDate).format("DD.MM.YY")}`}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <IconButton size="small" edge="end" onClick={() => handleLineClick(line.id)}>
                                                            <ArrowForwardIosIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                            <Divider />
                                        </Box>
                                    );
                                })}
                            </List>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </Layout>
    );
};

export default Warehouse;
